import { makeStyles } from "@material-ui/core";
import {
  AutocompleteInput,
  Datagrid,
  DateInput,
  Filter,
  FunctionField,
  Pagination,
  PaginationLimit,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  SearchInput,
  TextField,
} from "react-admin";
import React from "react";
import moment from "moment/moment";
import CustomDateField from "../../../components/CustomDateField";
import TypeField from "../../../components/TypeField";
import { SchoolIdSelected } from "utils/tools";
import EmptyComponent from "../../subcontractorPurchaseInvoices/components/EmptyComponent";
import { PaddingDenseStyledList } from "../../../components/ListWrapper";
import { StyledButton } from "../../../components/ListShowButton";
import SubcontractorTimesheetsPanel from "./SubcontractorTimesheetsPanel";
import { isEmpty } from "lodash";

interface ISubcontractorTimesheetsTable {
  isFixedHeight?: boolean;
  subcontractorId?: number;
  listProps?: any;
}

const useStyles = makeStyles({
  remarkCellStyle: {
    maxWidth: "400px",
  },
  lockIcon: {
    display: "block",
    textAlign: "center",
    padding: "6px 8px",
    paddingLeft: "0px",
    minWidth: "64px",
  },
});

const SubcontractorTimesheetFilter = (props: any) => {
  return (
    <Filter {...props}>
      <SearchInput source="search" alwaysOn />
      <DateInput source="Date" label="Date" alwaysOn />

      {props.isFromListView && (
        <ReferenceInput
          reference="assignments"
          source="AssignmentId"
          filter={{
            SchoolId: Number(SchoolIdSelected()),
            EstimatedStartedAt: props.filterValues.Date,
          }}
          alwaysOn
        >
          <AutocompleteInput optionText="routeName" />
        </ReferenceInput>
      )}

      {props.isNotFilteredBySubcontractor && (
        <ReferenceInput
          reference="subcontractors"
          source="SubcontractorId"
          alwaysOn
        >
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
      )}
    </Filter>
  );
};

const SubcontractorTimesheetsTable = ({
  isFixedHeight,
  subcontractorId,
  listProps,
}: ISubcontractorTimesheetsTable) => {
  const classes = useStyles();
  const isNotFilteredBySubcontractor = !subcontractorId;
  const isFromListView = listProps;

  const SubcontractorTimesheetEditButton = (
    record: ISubcontractorTimeSheet,
  ) => (
    <StyledButton
      variant="outlined"
      href={`/#/subcontractorTimesheets/${record.id}${
        !isNotFilteredBySubcontractor
          ? `?subcontractorId=${record.subcontractorId}`
          : ""
      }`}
      disabled={record.isInvoiced || record.isLocked}
    >
      {record.isInvoiced || record.isLocked ? "Locked" : "Edit"}
    </StyledButton>
  );

  const rowStyle = (record: any, index: number) => {
    const hasExistingAdhocFees =
      (record.oldSubcontractorAdhocFee &&
        !isEmpty(record.oldSubcontractorAdhocFee)) ||
      (record.newSubcontractorAdhocFee &&
        !isEmpty(record.newSubcontractorAdhocFee));

    const isLockedOrInvoiced = record.isInvoiced || record.isLocked;

    // 1. Has adhoc fees, not invoiced or locked
    if (hasExistingAdhocFees && !isLockedOrInvoiced) {
      return { backgroundColor: "rgba(167, 227, 252, 0.3)" }; // light blue
    }

    // 2. Has adhoc fees and is invoiced or locked
    if (hasExistingAdhocFees && isLockedOrInvoiced) {
      return { backgroundColor: "rgba(0, 0, 0, 0.14)" }; // darker gray
    }

    // 3. No adhoc fees, but invoiced or locked
    if (!hasExistingAdhocFees && isLockedOrInvoiced) {
      return { backgroundColor: "rgba(0, 0, 0, 0.04)" }; // light gray
    }

    // 4. Default: no adhoc fees and not invoiced or locked
    return {}; // default row color (white)
  };

  return (
    <ResourceContextProvider value="subcontractorTimesheets">
      <PaddingDenseStyledList
        basePath="/subcontractorTimesheets"
        actions={null}
        hasCreate={false}
        exporter={false}
        filters={
          <SubcontractorTimesheetFilter
            isNotFilteredBySubcontractor={isNotFilteredBySubcontractor}
            isFromListView={isFromListView}
          />
        }
        filter={{
          SchoolId: Number(SchoolIdSelected()),
        }}
        filterDefaultValues={{
          Date: `${moment().format("Y-MM-DD")}`,
          SubcontractorId: subcontractorId,
        }}
        sort={{ field: "id", order: "DESC" }}
        bulkActionButtons={false}
        perPage={50}
        {...listProps}
        pagination={
          <Pagination
            limit={isFixedHeight ? <EmptyComponent /> : <PaginationLimit />}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        }
        title={isNotFilteredBySubcontractor ? "Subcontractor Timesheets" : " "}
      >
        <Datagrid
          rowStyle={rowStyle}
          expand={<SubcontractorTimesheetsPanel />}
        >
          <TextField source="id" emptyText="-" />
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor"
            reference="subcontractors"
            emptyText="-"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <TextField source="routeName" emptyText="-" />
          <ReferenceField
            source="schoolId"
            label="School"
            reference="schools"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <CustomDateField source="date" label="Date" />
          <TextField source="totalStudent" emptyText="-" />
          <TypeField source="vehicleSize" type="subcontractor_vehicleType" />
          <TextField
            source="remark"
            emptyText="-"
            cellClassName={classes.remarkCellStyle}
          />
          <FunctionField
            label="Actions"
            render={(record: any) => (
              <SubcontractorTimesheetEditButton {...record} />
            )}
          />
        </Datagrid>
      </PaddingDenseStyledList>
    </ResourceContextProvider>
  );
};

export default SubcontractorTimesheetsTable;
